import { Printer, PrinterCheck, Loader2, AlertCircle, Clock } from "lucide-react";
import { isQZWebSocketActive, getAutoReconnectEnabled } from "@/lib/qz-tray";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { getPendingPrintCount } from "@/lib/print-queue";

interface PrinterStatusIndicatorProps {
  showLabel?: boolean;
  className?: string;
}

export function PrinterStatusIndicator({ 
  showLabel = false,
  className 
}: PrinterStatusIndicatorProps) {
  const [isConnected, setIsConnected] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const autoReconnect = getAutoReconnectEnabled();

  // Poll connection status and pending count periodically (read-only, no hook side effects)
  useEffect(() => {
    const check = () => {
      setIsConnected(isQZWebSocketActive());
      setPendingCount(getPendingPrintCount());
    };
    
    check();
    const interval = setInterval(check, 5000);
    return () => clearInterval(interval);
  }, []);

  if (!autoReconnect && !isConnected && pendingCount === 0) {
    return null;
  }

  const hasPending = pendingCount > 0;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div 
          className={cn(
            "flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors",
            !isConnected && !hasPending && "bg-muted text-muted-foreground",
            isConnected && !hasPending && "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
            isConnected && hasPending && "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
            !isConnected && hasPending && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
            className
          )}
        >
          {isConnected ? (
            hasPending ? (
              <Clock className="h-3.5 w-3.5" />
            ) : (
              <PrinterCheck className="h-3.5 w-3.5" />
            )
          ) : (
            <AlertCircle className="h-3.5 w-3.5" />
          )}
          {showLabel && (
            <span>
              {isConnected 
                ? hasPending 
                  ? `${pendingCount} pendente(s)` 
                  : "Impressora" 
                : "Sem Impressora"}
            </span>
          )}
          {!showLabel && hasPending && (
            <span className="tabular-nums">{pendingCount}</span>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {isConnected 
          ? hasPending
            ? `${pendingCount} impressão(ões) pendente(s) - processando...`
            : "Impressão conectada" 
          : hasPending
            ? `Desconectado - ${pendingCount} impressão(ões) na fila`
            : "Impressão desconectada - reconectando..."}
      </TooltipContent>
    </Tooltip>
  );
}
